import { AxiosResponse } from 'axios';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import _get from 'lodash.get';
import _set from 'lodash.set';
import { useForm } from 'react-hook-form';
import { meUpdate, updatePassword } from '../api';
import { handleApiGlobalError, syncUser } from '../helpers/asyncActions';
import { useGlobalContext } from '../stores/global';
import styles from './FormUpdateUserField.module.scss';

export interface Props {
  label: string;
  name: string;
  defaultValue?: string;
  updatable?: boolean;
  placeholder?: string;
  type?: 'text' | 'password' | 'select' | 'email';
  selectOptions?: { name: string; value: string }[];
}

export default function FormUpdateUserField({
  name,
  label,
  updatable = true,
  placeholder,
  type = 'text',
  ...props
}: Props) {
  const { user, dispatch } = useGlobalContext();
  const [editMode, setEditMode] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { register, handleSubmit, setValue } = useForm({
    defaultValues: user,
  });

  const handleFormSubmit = (data: any) => {
    let promise: Promise<AxiosResponse<any>>;
    const newData = { ...user };

    if (type === 'password') {
      promise = updatePassword(data?.password, data?.newPassword);
    } else {
      _set(newData, name, _get(data, name));
      promise = meUpdate(newData);
    }

    promise
      .then(() => syncUser(dispatch))
      .catch((err) => {
        resetValues();
        inputRef.current?.blur();
        throw err;
      })
      .catch(handleApiGlobalError.bind(null, dispatch))
      .finally(() => {
        inputRef.current?.blur();
        setEditMode(false);
      });
  };

  const resetValues = () => {
    setValue(name, _get(user, name));
  };

  useEffect(() => {
    if (props.selectOptions) resetValues();
  }, [props.selectOptions]);

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className={styles.container}
    >
      <h3>
        {label}
        <button
          type="button"
          onClick={() => {
            setEditMode(true);
            inputRef.current?.focus();
          }}
        >
          Editar
        </button>
      </h3>
      {type === 'select' ? (
        <select
          name={name}
          ref={register}
          className={clsx(editMode && styles.editMode)}
        >
          <option value="">{label}</option>
          {props.selectOptions?.map((el) => (
            <option key={el.value} value={el.value}>
              {el.name}
            </option>
          ))}
        </select>
      ) : (
        <>
          <input
            className={clsx(editMode && styles.editMode)}
            placeholder={
              type === 'password' ? 'Actual contraseña' : placeholder
            }
            autoComplete={type === 'password' ? 'off' : ''}
            name={name}
            onFocus={(e) => {
              // NOTE: Put cursor caret at the end
              const value = e.currentTarget.value;
              e.currentTarget.value = '';
              e.currentTarget.value = value;
            }}
            ref={(e) => {
              register(e);
              inputRef.current = e;
            }}
            type={type}
          />

          {type === 'password' && editMode && (
            <input
              className={styles.editMode}
              placeholder="Nueva contraseña"
              name="newPassword"
              ref={register}
              autoComplete="new-password"
              type="password"
            />
          )}
        </>
      )}

      {editMode && (
        <>
          <button
            className={styles.editBtns}
            type="button"
            onClick={() => {
              resetValues();
              setEditMode(false);
            }}
          >
            Cancelar
          </button>
          <button className={styles.editBtns} type="submit">
            Actualizar
          </button>
        </>
      )}
    </form>
  );
}
